import React, { createElement, Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { format, isValid, parseISO } from "date-fns";
import { enCA, frCA } from "date-fns/locale";
import { ArrowSortDown24, ChevronDown24, ChevronRight16, ChevronUp24, Close24, FlowArrow, FlowArrowShort, LaunchModal16, Search24, ShoppingBag24, ViewAll24 } from "./Icons";
import "./ApiDocTabs.scss";
import AppCodeSnippet from "../AppCodeSnippet/AppCodeSnippet";

type Block =
  | { type: "heading" | "subheading"; text: string }
  | { type: "paragraph"; html: string }
  | { type: "list"; items: string[]; ordered?: boolean; muted?: boolean; small?: boolean }
  | { type: "table"; columns: string[]; rows: string[][] }
  | { type: "code"; lines: string[]; language?: string }
  | { type: "links"; title?: string; items: { label: string; meta?: string; href: string }[] }
  | { type: "flow"; steps: string[] };

export interface DocSection { id: string; title: string; plain?: boolean; blocks?: Block[]; html?: string }

export interface CmsTab { introduction?: string; accordions?: { summary: string; details: string }[] }
type SectionData = DocSection[] | CmsTab[];

export const SECTION_KEYS = ["functional_capabilities", "request", "response", "use_cases", "error_codes"] as const;
export type SectionKey = (typeof SECTION_KEYS)[number];

export interface ApiDocument extends Partial<Record<SectionKey, SectionData>> {
  api_id: string;
  route_slug?: string;
  api_name: string;
  api_version: string;
  last_updated: string;
  breadcrumb: { label?: string; label_key?: string; href: string }[];
  introduction: { title: string; blocks: Block[] };
}


function blockText(b: Block): string {
  switch (b.type) {
    case "heading": case "subheading": return b.text;
    case "paragraph": return b.html;
    case "list": return b.items.join(" ");
    case "table": return [...b.columns, ...b.rows.flat()].join(" ");
    case "code": return b.lines.join(" ");
    case "links": return (b.title ?? "") + b.items.map((i) => i.label).join(" ");
    case "flow": return b.steps.join(" ");
  }
}

const stripTags = (html: string) => html.replace(/<[^>]*>/g, " ");

function isCmsTab(d: SectionData | undefined): d is CmsTab[] {
  return !!d && d.length > 0 && ("accordions" in d[0] || "introduction" in d[0]);
}

function getTab(doc: ApiDocument, key: SectionKey): { intro?: string; items: DocSection[] } {
  const d = doc[key];
  if (!d) return { items: [] };
  if (isCmsTab(d)) {
    const tab = d[0];
    return {
      intro: tab?.introduction,
      items: (tab?.accordions ?? []).map((a, i) => ({ id: `${key}-${i}`, title: a.summary, html: a.details })),
    };
  }
  return { items: d as DocSection[] };
}

const sectionText = (s: DocSection) => s.title + " " + (s.html ? stripTags(s.html) : (s.blocks ?? []).map(blockText).join(" "));

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

function useDialogFocus(ref: React.RefObject<HTMLElement>, onClose: () => void) {
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key !== "Tab" || !ref.current) return;
      const els = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (!els.length) return;
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      opener?.focus?.();
    };
  }, [ref, onClose]);
}

const VOID = new Set(["br", "hr", "img", "col", "wbr"]);
const BLOCKED = new Set(["script", "style", "iframe", "object", "embed"]);
const ATTR_MAP: Record<string, string> = { class: "className", colspan: "colSpan", rowspan: "rowSpan", for: "htmlFor" };

function WarningBanner({ children }: { children: ReactNode }) {
  return (
    <div  role="note">
      <div>{children}</div>
    </div>
  );
}

function isWarningBanner(el: Element) {
  return (el.getAttribute("class") ?? "").replace(/[\\"']|&quot;/g, "").split(/\s+/).includes("warning-banner");
}

const PAGE_EXT = new Set(["html", "htm", "php", "asp", "aspx", "jsp"]);
function fileNameOf(href: string): string | null {
  try {
    const url = new URL(href, window.location.href);
    if (!/^https?:$/.test(url.protocol)) return null;
    const name = decodeURIComponent(url.pathname.slice(url.pathname.lastIndexOf("/") + 1));
    const ext = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1).toLowerCase() : "";
    return ext && !PAGE_EXT.has(ext) ? name : null;
  } catch {
    return null;
  }
}

async function downloadFile(href: string, name: string) {
  try {
    const res = await fetch(href);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blobUrl = URL.createObjectURL(await res.blob());
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (err) {
    console.error("Download failed, opening instead:", err);
    window.open(href, "_blank", "noopener,noreferrer");
  }
}

function handleFileLinkClick(e: React.MouseEvent<HTMLElement>) {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const a = (e.target as HTMLElement).closest("a[href]") as HTMLAnchorElement | null;
  if (!a) return;
  const name = fileNameOf(a.getAttribute("href") ?? "");
  if (!name) return;
  e.preventDefault();
  downloadFile(a.href, a.getAttribute("download") || name);
}

function HtmlContent({ html, className }: { html: string; className?: string }) {
  const content = useMemo(() => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    let key = 0;
    const render = (node: Node): ReactNode => {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent;
      if (node.nodeType !== Node.ELEMENT_NODE) return null;
      const el = node as Element;
      const tag = el.tagName.toLowerCase();
      const k = key++;
      if (BLOCKED.has(tag)) return null;
      const kids = () => Array.from(el.childNodes).map(render);
      if (tag === "pre") return <AppCodeSnippet key={k} variant="apidoc" snippet={el.textContent ?? ""} />;
      if (tag === "div" && isWarningBanner(el)) return <WarningBanner key={k}>{kids()}</WarningBanner>;
      if (tag === "table") {
        const rows = Array.from(el.querySelectorAll("tr"));
        const isHeadRow = (tr: Element) => tr.closest("thead") !== null ||
          (tr.children.length > 0 && Array.from(tr.children).every((c) => c.tagName.toLowerCase() === "th"));
        let split = 0;
        while (split < rows.length && isHeadRow(rows[split])) split++;
        const renderRow = (tr: Element) => render(tr);
        const head = rows.slice(0, split).map(renderRow);
        const body = rows.slice(split).map(renderRow);
        return (
          <div key={k} className="ad-table-wrap">
            <table className="ad-table">
              {head.length > 0 && <thead>{head}</thead>}
              <tbody>{body}</tbody>
            </table>
          </div>
        );
      }
      const props: Record<string, unknown> = { key: k };
      for (const a of Array.from(el.attributes)) {
        const n = a.name.toLowerCase();
        if (n.startsWith("on") || n === "style") continue;
        if ((n === "href" || n === "src") && /^\s*javascript:/i.test(a.value)) continue;
        if (n === "class" && tag === "article") {
          const cls = a.value.split(/\s+/).filter((c) => c && c !== "align-center").join(" ");
          if (cls) props.className = cls;
          continue;
        }
        props[ATTR_MAP[n] ?? n] = a.value;
      }
      if (tag === "a" && props.target === "_blank") props.rel = "noopener noreferrer";
      if (tag === "a" && typeof props.href === "string") {
        const name = fileNameOf(props.href);
        if (name) { props.download = name; delete props.target; }
      }
      return VOID.has(tag) ? createElement(tag, props) : createElement(tag, props, kids());
    };
    return Array.from(doc.body.childNodes).map(render);
  }, [html]);
  return <div className={`ad-html ${className ?? ""}`}>{createElement(Fragment, null, content)}</div>;
}

function Blocks({ blocks }: { blocks: Block[] }) {
  return (
    <>
      {blocks.map((b, i) => {
        switch (b.type) {
          case "heading": return <h4 key={i} className="ad-h">{b.text}</h4>;
          case "subheading": return <h5 key={i} className="ad-sh">{b.text}</h5>;
          case "paragraph": return <p key={i} className="ad-p" dangerouslySetInnerHTML={{ __html: b.html }} />;
          case "list": {
            const Tag = b.ordered ? "ol" : "ul";
            return (
              <Tag key={i} className={`ad-list ${b.muted ? "is-muted" : ""} ${b.small ? "is-small" : ""}`}>
                {b.items.map((it, j) => <li key={j} dangerouslySetInnerHTML={{ __html: it }} />)}
              </Tag>
            );
          }
          case "table": return (
            <div key={i} className="ad-table-wrap">
              <table className="ad-table">
                <thead><tr>{b.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
                <tbody>{b.rows.map((r, j) => <tr key={j}>{r.map((c, k) => <td key={k} dangerouslySetInnerHTML={{ __html: c }} />)}</tr>)}</tbody>
              </table>
            </div>
          );
          case "code": return <AppCodeSnippet key={i} variant="apidoc" snippet={b.lines.join('\n')} />;
          case "links": return (
            <div key={i} className="ad-links">
              {b.title && <h5 className="ad-sh">{b.title}</h5>}
              {b.items.map((l) => (
                <div key={l.label}><a href={l.href} className="ad-link">{l.label}</a> <span className="ad-meta">{l.meta}</span></div>
              ))}
            </div>
          );
          case "flow": return (
            <div key={i} className="ad-flow">
              {b.steps.map((s, j) => (
                <div key={s} className="ad-flow__item">
                  <span className="ad-flow__step">{s}</span>
                  {j < b.steps.length - 1 && <span className="ad-flow__arrow"><FlowArrow className="ad-flow__h" /><FlowArrowShort className="ad-flow__v" /></span>}
                </div>
              ))}
            </div>
          );
          default: return null;
        }
      })}
    </>
  );
}

function AccordionItem({ section, open, onToggle }: { section: DocSection; open: boolean; onToggle: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const toggle = () => {
    onToggle();
    if (!open) requestAnimationFrame(() => {
      const el = ref.current;
      if (el && el.getBoundingClientRect().top < 0) el.scrollIntoView({ block: "start" });
    });
  };
  return (
    <div ref={ref} className={`ad-acc ${open ? "is-open" : ""} ${section.plain ? "is-plain" : ""}`}>
      <h3 className="ad-acc__heading">
        <button id={`head-${section.id}`} className="ad-acc__head" aria-expanded={open} aria-controls={`panel-${section.id}`} onClick={toggle}>
          <span>{section.title}</span>
          <ChevronDown24 className="ad-acc__chev" />
        </button>
      </h3>
      {open && <div id={`panel-${section.id}`} role="region" aria-labelledby={`head-${section.id}`} className="ad-acc__body">{section.html ? <HtmlContent html={section.html} /> : <Blocks blocks={section.blocks ?? []} />}</div>}
    </div>
  );
}

const availableTabs = (d: ApiDocument) => SECTION_KEYS.filter((k) => (d[k]?.length ?? 0) > 0);


const LAST_UPDATED_RE = /last updated:?\s*([\d]{4}-[\d]{2}-[\d]{2}|[\d]{1,2}[/-][\d]{1,2}[/-][\d]{2,4})/i;
const VERSION_RE = /(\d+(?:\.\d+)+)\s*$/;

function findRawIntro(raw: Record<string, unknown>): string | undefined {
  for (const key of SECTION_KEYS) {
    const tab = (raw[key] as CmsTab[] | undefined)?.[0];
    if (tab?.introduction) return tab.introduction;
  }
  return undefined;
}

function stripMetaOnlyIntro(sections: Partial<Record<SectionKey, SectionData>>): Partial<Record<SectionKey, SectionData>> {
  const next: Partial<Record<SectionKey, SectionData>> = { ...sections };
  for (const key of SECTION_KEYS) {
    const d = next[key];
    const tab = (d as CmsTab[] | undefined)?.[0];
    if (tab?.introduction && tab.introduction.trim().match(LAST_UPDATED_RE)?.[0] === tab.introduction.trim()) {
      next[key] = [{ ...tab, introduction: undefined }, ...(d as CmsTab[]).slice(1)];
    }
  }
  return next;
}

interface NormalizeOpts {
  urlVersion?: string;
  apiRouteSlug?: string;
}

function normalizeDoc(
  raw: Record<string, unknown>,
  t: (key: string, opts?: Record<string, unknown>) => string,
  lang: string,
  opts: NormalizeOpts = {}
): ApiDocument {
  const apiName: string = (raw.api_name as string) ?? "";
  const rawIntro = findRawIntro(raw);
  const dateMatch = rawIntro?.match(LAST_UPDATED_RE);
  const versionMatch = apiName.match(VERSION_RE);
  const apiVersion = (raw.api_version as string) ?? opts.urlVersion ?? versionMatch?.[1] ?? "";
  const apiSlug = (raw.route_slug as string | undefined) ?? opts.apiRouteSlug ?? apiName.replace(/\s+\d+(?:\.\d+)*\s*$/, "").toLowerCase().replace(/\s+/g, "");

  const ndcApisHref = apiVersion ? `/${lang}/api/documentation/ndcapis/${apiVersion}` : `/${lang}/api/documentation`;
  const apiHref = apiVersion && apiSlug ? `${ndcApisHref}/${apiSlug}/functionality` : window.location.pathname;

  return {
    ...stripMetaOnlyIntro(raw as Partial<Record<SectionKey, SectionData>>),
    api_id: raw.route_slug != null ? String(raw.route_slug) : raw.api_id != null ? String(raw.api_id) : raw.id != null ? String(raw.id) : apiName,
    route_slug: raw.route_slug as string | undefined,
    api_name: apiName,
    api_version: apiVersion,
    last_updated: (raw.last_updated as string) ?? dateMatch?.[1] ?? "",
    breadcrumb: (raw.breadcrumb as ApiDocument["breadcrumb"]) ?? [
      { label: t("apiDoc.breadcrumb.home"), href: `/` },
      { label: t("apiDoc.breadcrumb.documentation"), href: ndcApisHref },
      { label: apiName, href: apiHref },
    ],
    introduction: (raw.introduction as ApiDocument["introduction"]) ?? {
      title: apiName,
      blocks: rawIntro ? [{ type: "paragraph", html: rawIntro }] : [],
    },
  };
}

function pushSublevel(key: SectionKey) {
  const segs = window.location.pathname.split("/").filter(Boolean);
  if (segs.length >= 4) {
    segs[segs.length - 1] = key;
    window.history.pushState({}, "", "/" + segs.join("/"));
    window.dispatchEvent(new PopStateEvent("popstate"));
  }
}
function pushApiSection(slug: string, key: SectionKey) {
  const segs = window.location.pathname.split("/").filter(Boolean);
  if (segs.length >= 5) {
    segs[segs.length - 2] = slug;
    segs[segs.length - 1] = key;
    window.history.pushState({}, "", "/" + segs.join("/"));
    window.dispatchEvent(new PopStateEvent("popstate"));
  }
}
function pushPath(path: string) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
const sublevelFromUrl = (): SectionKey | undefined => {
  const last = window.location.pathname.split("/").filter(Boolean).pop();
  return SECTION_KEYS.find((k) => k === last);
};

function SideNav({ docs, activeApi, activeSection, onSelect, tabLabel, idPrefix }: {
  docs: ApiDocument[]; activeApi: string; activeSection: SectionKey; onSelect: (api: string, s: SectionKey) => void;
  tabLabel: (k: SectionKey) => string; idPrefix: string;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({ [activeApi]: true });
  useEffect(() => {
    setExpanded((e) => (e[activeApi] ? e : { ...e, [activeApi]: true }));
  }, [activeApi]);
  return (
    <nav className="ad-nav" aria-labelledby={`${idPrefix}-title`}>
      <h2 id={`${idPrefix}-title`} className="ad-nav__title"><ViewAll24 /> {t("apiDoc.allWebServices")}</h2>
      {docs.map((d) => {
        const isOpen = !!expanded[d.api_id];
        const listId = `${idPrefix}-${d.api_id}`.replace(/[^\w-]/g, "-");
        return (
          <div key={d.api_id}>
            <button className={`ad-nav__api ${d.api_id === activeApi ? "is-active" : ""}`} aria-expanded={isOpen}
              aria-controls={listId}
              onClick={() => setExpanded((e) => ({ ...e, [d.api_id]: !isOpen }))}>
              <ShoppingBag24 className="ad-nav__bag" /><span className="ad-nav__label">{d.api_name}</span> <ArrowSortDown24 className={`ad-nav__sort ${isOpen ? "rot" : ""}`} />
            </button>
            {isOpen && (
              <ul id={listId} className="ad-nav__list" aria-label={d.api_name}>
                {availableTabs(d).map((k) => (
                  <li key={k}>
                    <button className={`ad-nav__item ${d.api_id === activeApi && k === activeSection ? "is-active" : ""}`}
                      aria-current={d.api_id === activeApi && k === activeSection ? "page" : undefined}
                      onClick={() => onSelect(d.api_id, k)}>
                      {tabLabel(k)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </nav>
  );
}

function IntroModal({ doc, onClose }: { doc: ApiDocument; onClose: () => void }) {
  const { t } = useTranslation();
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);
  useDialogFocus(dialogRef, onClose);
  useEffect(() => { closeRef.current?.focus(); }, []);
  return (
    <div className="ad-overlay" onClick={onClose}>
      <div ref={dialogRef} className="ad-modal" role="dialog" aria-modal="true" aria-labelledby="intro-title" onClick={(e) => e.stopPropagation()}>
        <div className={`ad-modal__head ${scrolled ? "is-scrolled" : ""}`}>
          <h2 id="intro-title">{doc.introduction.title}</h2>
          <button ref={closeRef} className="ad-icon-btn" aria-label={t("apiDoc.close")} onClick={onClose}><Close24 /></button>
        </div>
        <div className="ad-modal__body" tabIndex={0} onScroll={(e) => setScrolled(e.currentTarget.scrollTop > 0)}><Blocks blocks={doc.introduction.blocks} /></div>
      </div>
    </div>
  );
}

function NavDrawer({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  useDialogFocus(ref, onClose);
  useEffect(() => { closeRef.current?.focus(); }, []);
  return (
    <div className="ad-overlay ad-overlay--top" onClick={onClose}>
      <div ref={ref} className="ad-drawer" role="dialog" aria-modal="true" aria-labelledby="ad-drawer-nav-title" onClick={(e) => e.stopPropagation()}>
        <button ref={closeRef} className="ad-icon-btn ad-drawer__close" aria-label={t("apiDoc.closeWebServices")} onClick={onClose}><Close24 /></button>
        {children}
      </div>
    </div>
  );
}

export interface ApiDocPageProps {
  docs: Partial<ApiDocument>[];
  tabs?: Partial<Record<SectionKey, string>>;
  selectedTab?: string;
  setCurrLinks?: (fn: (prev: string[]) => string[]) => void;
  urlVersion?: string;
  apiRouteSlug?: string;
}

export default function ApiDocPage({ docs: rawDocs, tabs, selectedTab, setCurrLinks, urlVersion, apiRouteSlug }: ApiDocPageProps) {
  const { t, i18n } = useTranslation();
  const docs = useMemo(
    () => rawDocs.map((d) => normalizeDoc(d as Record<string, unknown>, t, i18n.language, { urlVersion, apiRouteSlug })),
    [rawDocs, t, i18n.language, urlVersion, apiRouteSlug]
  );
  const [apiId, setApiId] = useState(() => (docs.find((d) => d.route_slug === apiRouteSlug) ?? docs[0]).api_id);
  const tabsFor = useCallback((d: ApiDocument) => {
    const avail = availableTabs(d);
    return tabs ? avail.filter((k) => k in tabs) : avail;
  }, [tabs]);
  const resolveTab = useCallback((d: ApiDocument, wanted?: string): SectionKey => {
    const list = tabsFor(d);
    if (d.api_name === "Scenarios") return list[0] ?? "functional_capabilities";
    return list.find((k) => k === wanted) ?? list[0] ?? "functional_capabilities";
  }, [tabsFor]);
  const [section, setSection] = useState<SectionKey>(() => resolveTab(docs[0], selectedTab ?? sublevelFromUrl()));
  const tabLabel = (k: SectionKey) => tabs?.[k] ?? t(`apiDoc.sections.${k}`)!;
  const [openIds, setOpenIds] = useState<Set<string>>(() => new Set());
  const [query, setQuery] = useState("");
  const [introOpen, setIntroOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showTop, setShowTop] = useState(false);

  const doc = docs.find((d) => d.api_id === apiId) ?? docs[0];

  useEffect(() => {
    if (!docs.some((d) => d.api_id === apiId)) {
      setApiId((docs.find((d) => d.route_slug === apiRouteSlug) ?? docs[0]).api_id);
    }
  }, [docs, apiId, apiRouteSlug]);

  useEffect(() => {
    const match = apiRouteSlug ? docs.find((d) => d.route_slug === apiRouteSlug) : undefined;
    if (match) setApiId(match.api_id);
  }, [apiRouteSlug, docs]);

  const syncKeyRef = useRef<string>("");
  useEffect(() => {
    const key = `${doc.api_id}::${selectedTab ?? ""}`;
    if (syncKeyRef.current === key) return;
    syncKeyRef.current = key;
    setSection(resolveTab(doc, selectedTab ?? sublevelFromUrl()));
    setOpenIds(new Set());
  }, [selectedTab, doc, resolveTab]);
  useEffect(() => {
    const onPop = () => { const s = sublevelFromUrl(); if (s) setSection(resolveTab(doc, s)); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [doc, resolveTab]);

  const tab = useMemo(() => getTab(doc, section), [doc, section]);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const items = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tab.items;
    return tab.items.filter((s) => sectionText(s).toLowerCase().includes(q));
  }, [tab, query]);

  const titleRef = useRef<HTMLHeadingElement>(null);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const closeIntro = useCallback(() => setIntroOpen(false), []);
  const select = (api: string, s: SectionKey) => {
    const changed = api !== apiId || s !== section;
    setApiId(api); setSection(s); setDrawerOpen(false);
    // Move focus to the section heading so keyboard and screen reader users land on the new content
    requestAnimationFrame(() => titleRef.current?.focus());
    if (!changed) return;
    setOpenIds(new Set());
    const slug = docs.find((d) => d.api_id === api)?.route_slug;
    setCurrLinks?.((prev) => { const next = [...prev]; if (slug) next[1] = slug; next[2] = s; return next; });
    if (slug && api !== apiId) pushApiSection(slug, s);
    else pushSublevel(s);
  };

  const parsedDate = doc.last_updated ? parseISO(doc.last_updated) : undefined;
  const dateStr = parsedDate && isValid(parsedDate) ? format(parsedDate, t("apiDoc.dateFormat"), { locale: i18n.language === "fr" ? frCA : enCA }) : undefined;

  return (
    <div className="ad-page" onClick={handleFileLinkClick}>
      <div className="ad-container">
        <nav className="ad-crumbs" aria-label={t("apiDoc.breadcrumb.label")}>
          <ol>
            {doc.breadcrumb.map((c, i) => (
              <li key={i}>
                <a
                  href={c.href}
                  onClick={(e) => {
                    e.preventDefault();
                    pushPath(c.href);
                  }}
                >
                  {c.label_key ? t(c.label_key) : c.label}
                </a>
                <ChevronRight16 />
              </li>
            ))}
            <li aria-current="page">{tabLabel(section)}</li>
          </ol>
        </nav>

        {doc.api_version && <span className="ad-badge">{t("apiDoc.versionBadge", { version: doc.api_version })}</span>}
        <h1 className="ad-title">{doc.api_name}</h1>
        <p className="ad-intro">
          {t("apiDoc.whatIs", { name: doc.api_name, version: doc.api_version })}{" "}
          <button className="ad-link ad-link--btn" aria-haspopup="dialog" onClick={() => setIntroOpen(true)}>
            {t("apiDoc.seeIntroduction")} <LaunchModal16 />
          </button>
        </p>
        {dateStr && <p className="ad-updated">{t("apiDoc.lastUpdated", { date: dateStr })}</p>}

        <div className="ad-search" role="search">
          <Search24 />
          <label htmlFor="ad-search-input" className="ad-sr-only">{t("apiDoc.searchLabel")}</label>
          <input id="ad-search-input" type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("apiDoc.searchPlaceholder")} />
          {query && <button className="ad-icon-btn" aria-label={t("apiDoc.clearSearch")} onClick={() => setQuery("")}><Close24 /></button>}
        </div>
        <p className="ad-sr-only" role="status" aria-live="polite">
          {query ? t("apiDoc.resultsCount", { count: items.length }) : ""}
        </p>

        <button className="ad-ws-btn" aria-haspopup="dialog" onClick={() => setDrawerOpen(true)}>
          <ViewAll24 /> {t("apiDoc.allWebServices")} <ChevronRight16 />
        </button>

        <div className="ad-layout">
          <aside className="ad-aside">
            <SideNav docs={docs} activeApi={apiId} activeSection={section} onSelect={select} tabLabel={tabLabel} idPrefix="ad-aside-nav" />
          </aside>

          <section className="ad-card" aria-labelledby="ad-section-title">
            <h2 id="ad-section-title" ref={titleRef} tabIndex={-1} className="ad-card__title">{tabLabel(section)}</h2>
            {items.length === 0 && <p className="ad-p ad-empty">{t("apiDoc.noResults", { q: query })}</p>}
            {items.map((s) => (
              <AccordionItem key={s.id} section={s} open={openIds.has(s.id) || (!!query && items.length <= 2)}
                onToggle={() => setOpenIds((prev) => { const next = new Set(prev); if (next.has(s.id)) next.delete(s.id); else next.add(s.id); return next; })} />
            ))}
          </section>
        </div>
      </div>

      {drawerOpen && (
        <NavDrawer onClose={closeDrawer}>
          <SideNav docs={docs} activeApi={apiId} activeSection={section} onSelect={select} tabLabel={tabLabel} idPrefix="ad-drawer-nav" />
        </NavDrawer>
      )}

      {introOpen && <IntroModal doc={doc} onClose={closeIntro} />}

      {showTop && (
        <button className="ad-top" aria-label={t("apiDoc.backToTop")} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
          <ChevronUp24 />
        </button>
      )}
    </div>
  );
}
